import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { requerimientoService } from '../services';
import './BandejaEntrada.css';

const BandejaEntrada = () => {
  const [requerimientos, setRequerimientos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const itemsPorPagina = 10;

  useEffect(() => {
    cargarBandeja();
  }, []);

  const cargarBandeja = async () => {
    try {
      const data = await requerimientoService.bandejaEntrada();
      setRequerimientos(data.results || data);
    } catch (error) {
      console.error('Error al cargar bandeja:', error);
    } finally {
      setCargando(false);
    }
  };

  const getEstadoColor = (estado) => {
    const colores = {
      elaborado: '#2196f3',
      por_revision: '#ff9800',
      aprobado_vicepresidencia: '#4caf50',
      rechazado_vicepresidencia: '#f44336',
      recibido_administracion: '#00bcd4',
      aprobado_administracion: '#8bc34a',
      rechazado_administracion: '#f44336',
      pagado_parcial: '#7c3aed',
      pagado: '#9c27b0',
    };
    return colores[estado] || '#9e9e9e';
  };

  const getEstadoLabel = (estado) => {
    const labels = {
      elaborado: 'Elaborado',
      por_revision: 'Por Revisión',
      aprobado_vicepresidencia: 'Aprobado por VP',
      rechazado_vicepresidencia: 'Rechazado por VP',
      recibido_administracion: 'Recibido por Administración',
      aprobado_administracion: 'Aprobado por Administración',
      rechazado_administracion: 'Rechazado por Administración',
      pagado_parcial: 'Pagado Parcial',
      pagado: 'Pagado',
    };
    return labels[estado] || estado;
  };

  const getCriticidadBadge = (criticidad) => {
    const badges = {
      alta:  { label: 'Alta',  emoji: '🔴', color: '#fff5f5', border: '#feb2b2', text: '#c53030' },
      media: { label: 'Media', emoji: '🟡', color: '#fefcbf', border: '#faf089', text: '#b7791f' },
      baja:  { label: 'Baja',  emoji: '🟢', color: '#f0fff4', border: '#9ae6b4', text: '#276749' },
    };
    const b = badges[criticidad];
    if (!b) return <span style={{ color: '#a0aec0', fontSize: '12px' }}>Pendiente</span>;
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '700',
        backgroundColor: b.color,
        border: `1px solid ${b.border}`,
        color: b.text,
        whiteSpace: 'nowrap',
        lineHeight: '1.4',
      }}>
        {b.label} <span style={{ fontSize: '11px', lineHeight: 1 }}>{b.emoji}</span>
      </span>
    );
  };

  if (cargando) {
    return <div className="loading">Cargando...</div>;
  }

  const filtrados = requerimientos.filter(req => {
    const texto = `${req.correlativo || req.id} ${req.solicitante_nombre} ${req.solicitante_apellido} ${req.vicepresidencia_nombre} ${req.gerencia_nombre} ${getEstadoLabel(req.estado_actual)}`.toLowerCase();
    return texto.includes(busqueda.toLowerCase());
  });

  const totalPaginas = Math.ceil(filtrados.length / itemsPorPagina);
  const indiceUltimo = paginaActual * itemsPorPagina;
  const indicePrimero = indiceUltimo - itemsPorPagina;
  const reqsPaginados = filtrados.slice(indicePrimero, indiceUltimo);

  return (
    <div className="bandeja-entrada">
      <h2 className="bandeja-entrada-titulo">
        <span className="titulo-icono">📥</span>
        Bandeja de Entrada
      </h2>

      <div className="be-table-wrapper">
        {/* ── Header ── */}
        <div className="be-card-header">
          <h3>Listado de Requerimientos</h3>
          <span className="be-count-pill">{filtrados.length} EN TOTAL</span>
        </div>

        {/* ── Barra de búsqueda ── */}
        {requerimientos.length > 0 && (
          <div className="be-search-bar">
            <input
              type="text"
              placeholder="🔍 Buscar por código, solicitante, o estatus..."
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value);
                setPaginaActual(1);
              }}
              className="be-search-input"
            />
          </div>
        )}

        {/* ── Tabla / Estado vacío ── */}
        {filtrados.length === 0 ? (
          <div className="be-empty-state">
            <p>No se encontraron requerimientos pendientes en su bandeja de entrada.</p>
          </div>
        ) : (
          <div className="be-table-responsive">
            <table className="be-table">
              <thead>
                <tr>
                  <th>Identificador</th>
                  <th>Solicitante</th>
                  <th>VP Emisora</th>
                  <th>Gerencia</th>
                  <th>Monto (Bs.)</th>
                  <th>Criticidad</th>
                  <th>Estatus</th>
                  <th>Fecha Creación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {reqsPaginados.map(req => (
                  <tr key={req.id}>
                    <td>
                      <span className="be-id-cell">
                        {req.correlativo}
                      </span>
                    </td>
                    <td>{req.solicitante_nombre} {req.solicitante_apellido}</td>
                    <td>{req.vicepresidencia_nombre}</td>
                    <td>{req.gerencia_nombre}</td>
                    <td>
                      <span className="be-monto-cell">
                        {parseFloat(req.monto_bs).toLocaleString('es', { minimumFractionDigits: 2 })} Bs.
                      </span>
                    </td>
                    <td>{getCriticidadBadge(req.criticidad)}</td>
                    <td>
                      <span
                        className="be-estado-badge"
                        style={{ backgroundColor: getEstadoColor(req.estado_actual) }}
                      >
                        {req.estado_display || getEstadoLabel(req.estado_actual)}
                      </span>
                    </td>
                    <td>{new Date(req.fecha_creacion).toLocaleDateString('es')}</td>
                    <td>
                      <Link
                        to={`/requerimientos/${req.correlativo || req.id}`}
                        className="be-btn-link"
                      >
                        Ver Detalles
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Paginación ── */}
        {filtrados.length > 0 && (
          <div className="be-pagination-bar">
            <button
              onClick={() => setPaginaActual(prev => Math.max(prev - 1, 1))}
              disabled={paginaActual === 1}
              className="be-pagination-btn"
            >
              Anterior
            </button>
            <span className="be-pagination-info">
              Página {paginaActual} de {totalPaginas}
            </span>
            <button
              onClick={() => setPaginaActual(prev => Math.min(prev + 1, totalPaginas))}
              disabled={paginaActual >= totalPaginas}
              className="be-pagination-btn"
            >
              Siguiente
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BandejaEntrada;
